/**
 * @fileoverview Composable for widget drag functionality
 * @module composables/useWidgetDrag
 *
 * Manages drag-and-drop behavior for the webchat widget,
 * including launcher and chat window positioning.
 *
 * @vue3migration - Phase 3: Extracted from App.vue for better code organization
 */
import { ref, reactive, computed, type ComputedRef, type Ref, type ShallowRef } from 'vue'
import type { ComponentPublicInstance } from 'vue'

/**
 * Configuration options for widget drag behavior
 */
interface UseWidgetDragOptions {
  /** Whether widget dragging is enabled in settings */
  isWidgetDraggable: ComputedRef<boolean>
  /** Whether UI beta mode is enabled */
  isUiBetaEnabled: ComputedRef<boolean>
  /** Whether device is mobile */
  isMobile: boolean
  /** Window size setting ('wide' | 'normal') for widget width calculation */
  windowSize: ComputedRef<string>
  /** Template ref for launcher component */
  launcherRef: Readonly<ShallowRef<ComponentPublicInstance | null>>
  /** Template ref for chat window component */
  chatWindowRef: Readonly<ShallowRef<ComponentPublicInstance | null>>
}

/**
 * Widget position coordinates
 */
interface WidgetPosition {
  left: number | null
  top: number | null
}

/**
 * Composable for widget drag functionality
 *
 * Provides state and handlers for dragging the webchat launcher and
 * positioning the chat window relative to the launcher.
 *
 * @param options - Configuration options for drag behavior
 * @returns Drag state and handlers
 *
 * @example
 * ```ts
 * const {
 *   isElementReadyToDrag,
 *   widgetPos,
 *   handleGrabElement,
 * } = useWidgetDrag({
 *   isWidgetDraggable: computed(() => settings.value.isWidgetDraggable),
 *   isUiBetaEnabled,
 *   isMobile: $device?.Mobile ?? false,
 *   windowSize: computed(() => settings.value.window),
 *   launcherRef: launcher,
 *   chatWindowRef: chatwindow,
 * })
 * ```
 */
export function useWidgetDrag(options: UseWidgetDragOptions) {
  const {
    isWidgetDraggable,
    isUiBetaEnabled,
    isMobile,
    windowSize,
    launcherRef,
    chatWindowRef,
  } = options

  // ====================
  // State
  // ====================

  /** Whether widget is currently being dragged */
  const isElementReadyToDrag = ref(false)

  /** Mouse/touch offset from element corner during drag */
  const elementOffset = ref<[number, number]>([0, 0])

  /** ID of element being dragged */
  const dragElementId = ref<string | null>(null)

  /** Chat window position when using draggable mode */
  const widgetPos = reactive<WidgetPosition>({
    left: null,
    top: null,
  })

  // ====================
  // Computed
  // ====================

  /**
   * Get DOM element being dragged by ID
   */
  const elementDOM = computed(() =>
    dragElementId.value ? document.getElementById(dragElementId.value) : null
  )

  // ====================
  // Internal Methods
  // ====================

  /**
   * Handle element dragging (mousemove/touchmove)
   */
  function handleDragElement(e: MouseEvent | TouchEvent) {
    if (!isElementReadyToDrag.value) {
      isElementReadyToDrag.value = true
    }

    const element = elementDOM.value
    if (!element) return

    if (!element.style.right || !element.style.bottom) {
      element.style.right = "auto"
      element.style.bottom = "auto"
    }

    if (isElementReadyToDrag.value) {
      const isTouchEvent = e.type?.startsWith("touch")
      let clientX: number
      let clientY: number

      if (isTouchEvent) {
        const touchEvent = e as TouchEvent
        clientX = touchEvent.touches[0].clientX
        clientY = touchEvent.touches[0].clientY
      } else {
        const mouseEvent = e as MouseEvent
        clientX = mouseEvent.clientX
        clientY = mouseEvent.clientY
      }

      element.style.left = clientX - elementOffset.value[0] + "px"
      element.style.top = clientY - elementOffset.value[1] + "px"
    }
  }

  /**
   * Change widget position based on launcher position
   * Positions chat window relative to the dragged launcher
   */
  function changeWidgetPosition(windowWidth: number, windowHeightValue: number) {
    const element = elementDOM.value
    if (!element) return

    const iconLeftPos = parseInt(element.style.left)
    const iconTopPos = parseInt(element.style.top)
    const iconElement = launcherRef.value?.$el as HTMLElement | undefined
    const widgetElement = chatWindowRef.value?.$el as HTMLElement | undefined
    const widgetWidth = windowSize.value === "wide" ? 550 : 420
    const widgetHeight = 550
    let widgetTopPos = iconTopPos - 500
    let widgetLeftPos: number

    if (widgetElement) {
      widgetElement.style.transition = "all 0.5s"
    }

    if (iconLeftPos > windowWidth / 2) {
      widgetLeftPos = iconLeftPos - widgetWidth + 10
    } else {
      widgetLeftPos = iconLeftPos + (iconElement?.offsetWidth || 0) + 10
    }

    if (widgetTopPos < 0) {
      widgetTopPos = 10
    } else if (widgetTopPos > windowHeightValue - widgetHeight) {
      widgetTopPos = windowHeightValue - (widgetHeight + 15)
    }

    widgetPos.left = widgetLeftPos
    widgetPos.top = widgetTopPos
  }

  /**
   * Handle stop grabbing element (mouseup/touchend)
   * Snaps element to viewport edges if dragged outside
   */
  function handleStopGrabElement() {
    const element = elementDOM.value
    if (!element) return

    const elementStyle = window.getComputedStyle(element)
    const elementLeftPosition = parseInt(elementStyle.left)
    const elementTopPosition = parseInt(elementStyle.top)
    const windowWidth = window.innerWidth - parseInt(elementStyle.width)
    const windowHeightValue = window.innerHeight - parseInt(elementStyle.height)
    element.style.transition = "all 0.5s"

    let fixedLeft: number | undefined
    let fixedTop: number | undefined

    if (elementLeftPosition > windowWidth) {
      fixedLeft = dragElementId.value === "keyreply-launcher" ? windowWidth - 25 : windowWidth
    }
    if (elementLeftPosition < 0) {
      fixedLeft = dragElementId.value === "keyreply-launcher" ? 10 : 0
    }
    if (elementTopPosition < 0) {
      fixedTop = dragElementId.value === "keyreply-launcher" ? 10 : 0
    }
    if (elementTopPosition > windowHeightValue) {
      fixedTop = dragElementId.value === "keyreply-launcher" ? windowHeightValue - 25 : windowHeightValue
    }

    if (fixedLeft !== undefined) {
      element.style.left = fixedLeft + "px"
    }
    if (fixedTop !== undefined) {
      element.style.top = fixedTop + "px"
    }

    // Clean up document event listeners
    document.ontouchmove = null
    document.ontouchend = null
    document.onmousemove = null
    document.onmouseup = null

    setTimeout(() => {
      isElementReadyToDrag.value = false
    }, 0)

    // Update chat window position when launcher is dragged (desktop only)
    if (dragElementId.value === "keyreply-launcher" && !isMobile) {
      changeWidgetPosition(windowWidth, windowHeightValue)
    }
  }

  // ====================
  // Public Methods
  // ====================

  /**
   * Handle element grab for dragging (mousedown/touchstart)
   *
   * @param event - Mouse or touch event
   * @param elementId - ID of the element being grabbed
   */
  function handleGrabElement({ event, elementId }: { event: MouseEvent | TouchEvent; elementId: string }) {
    // Check if dragging is enabled
    if (
      !isWidgetDraggable.value ||
      !((isUiBetaEnabled.value && !isMobile) || elementId === "keyreply-launcher")
    ) {
      return
    }

    let clientX: number
    let clientY: number

    if (event.type === "touchstart") {
      const touchEvent = event as TouchEvent
      clientX = touchEvent.touches[0].clientX
      clientY = touchEvent.touches[0].clientY
    } else {
      const mouseEvent = event as MouseEvent
      clientX = mouseEvent.clientX
      clientY = mouseEvent.clientY
    }

    dragElementId.value = elementId
    const element = elementDOM.value
    if (!element) return

    element.style.transition = "all 0s"
    elementOffset.value = [
      clientX - element.offsetLeft,
      clientY - element.offsetTop,
    ]

    // Set up document event listeners for drag tracking
    if (event.type === "touchstart") {
      document.ontouchmove = handleDragElement as any
      document.ontouchend = handleStopGrabElement as any
    } else {
      document.onmousemove = handleDragElement as any
      document.onmouseup = handleStopGrabElement as any
    }
  }

  return {
    // State
    isElementReadyToDrag,
    widgetPos,
    // Methods
    handleGrabElement,
  }
}
